import { startCase } from 'es-toolkit';
import React from 'react';
import AddItemIcon from '@mui/icons-material/AddCircle';

import { useAppMonitor } from '../../../../monitor';
import {
  startInsertGusset,
  startInsertLoadCase,
  startInsertMember,
  startInsertPlatePair,
  startInsertReferenceLine,
} from '../../../../store/actions/insertActions';
import { startInsertComponentDesign } from '../../../../store/actions/insertComponentDesignActions';
import { startInsertingBeam, startInsertingWall } from '../../../../store/actions/layout/bearingEnvelopeActions';
import { startInsertingCeilingPlane } from '../../../../store/actions/layout/ceilingPlaneActions';
import { startInsertingCeilingTray } from '../../../../store/actions/layout/ceilingTrayActions';
import { startInsertingFeaturePatternContainer } from '../../../../store/actions/layout/featurePatternContainerActions';
import { startInsertingRoofPlane } from '../../../../store/actions/layout/roofPlaneActions';
import { startInsertTrussEnvelope } from '../../../../store/actions/layout/trussEnvelopeActions';
import { startInsertWorkLine } from '../../../../store/actions/layout/workLineActions';
import { showNotOnMobileDialog } from '../../../../store/actions/popupActions';
import { startInsertTrussGroup } from '../../../../store/actions/trussGroups';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { isParagonRepairJobOpen } from '../../../../store/selectors/jobContextSelectors';
import {
  applicationOnInsertingJackGirderConnection,
  applicationOnInsertingManualLoad,
  applicationOnInsertingMovingLoadSettings,
  applicationOnInsertingParapetLoadSettings,
  applicationStartInsertBearing,
} from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector, useIsClientInMobileMode } from '../../../hooks';
import { SceneMenuItem } from '../common/SceneMenuItem';

const creationActions = {
  bearing: () => applicationStartInsertBearing(),
  componentDesign: startInsertComponentDesign,
  brace: () => startInsertMember({ isBrace: true }),
  gusset: () => startInsertGusset(),
  platePair: () => startInsertPlatePair(),
  referenceLine: () => startInsertReferenceLine(),
  loadCase: startInsertLoadCase,
  manualLoad: () => applicationOnInsertingManualLoad(),
  member: () => startInsertMember({ isBrace: false }),
  jackGirderConnection: () => applicationOnInsertingJackGirderConnection(),
  movingLoad: () => applicationOnInsertingMovingLoadSettings(),
  parapetLoad: () => applicationOnInsertingParapetLoadSettings(),
  workLine: () => startInsertWorkLine(),
  beam: () => startInsertingBeam(),
  bearingEnvelope: () => startInsertingWall(),
  roofPlane: () => startInsertingRoofPlane(),
  ceilingPlane: () => startInsertingCeilingPlane(),
  ceilingTray: () => startInsertingCeilingTray(),
  featurePatternContainer: () => startInsertingFeaturePatternContainer(),
  trussEnvelope: () => startInsertTrussEnvelope(),
  trussGroup: () => startInsertTrussGroup(),
};

export function AddItemMenuItem(props: {
  type: keyof typeof creationActions;
  parentGuid?: string;
  userVisibleTerm?: string;
  disabled?: boolean;
}) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const mobile = useIsClientInMobileMode();

  const isInJobContext = useAppSelector((state) => state.application.openContext?.type === 'job');
  const isInParagonRepairJob = useAppSelector(isParagonRepairJobOpen);

  const componentDesign = useAppSelector(getActiveComponentDesign)!;

  const isUpdatePending = useAppSelector((state) => {
    if (props.type !== 'member') return false;

    const asyncRequest = state.asyncRequests[componentDesign.guid];
    return asyncRequest != null && asyncRequest.type !== 'updateFeatures';
  });

  if (isInJobContext && !isInParagonRepairJob && props.type === 'componentDesign') return null;

  const userVisibleTerm = props.userVisibleTerm ?? startCase(props.type);

  return (
    <SceneMenuItem
      icon={<AddItemIcon />}
      text={`Create ${userVisibleTerm}`}
      disabled={isUpdatePending || props.disabled}
      onClick={() => {
        if (mobile) {
          dispatch(showNotOnMobileDialog('Creating items is not yet available on mobile.'));
          return;
        }

        dispatch(creationActions[props.type](props.parentGuid));
        monitor.recordInteraction(`Create New ${userVisibleTerm} menu item clicked`, { type: props.type });
      }}
    />
  );
}
